import React, { useEffect, useState } from "react";
import ProgressDataCard from "./ProgressDataCard";
import { formatInTimeZone } from "date-fns-tz";
import { eachMonthOfInterval } from "date-fns/esm";
import ProgressHeaderCard from "./ProgressHeaderCard";
import DeltaMathToggle from "../../../shared/DeltaMathToggle";
import ProgressScatterPlot from "./ProgressScatterPlot";
import DeltaMathAccordion from "../../../shared/DeltaMathAccordion";
import { Transition } from "@headlessui/react";
import { demoString } from "../../../utils/demo";
import GridIcon from "../../../shared/icons/GridIcon";
import GraphIcon from "../../../shared/icons/GraphIcon";
import { Tooltip } from "../../../shared/Tooltip";
import {
  ProgressGraphDataInterface,
  ProgressData,
  ProgressDataEntry,
  ProgressLocalStorageInterface,
  RowDataInterface,
  SchoolData,
  SectionData,
  TeacherData,
  ProgressMonthData,
  ProgressMonthDataEntry,
} from "../ReportTypes";
import { DAY, ProgressSeriesLimit } from "../ReportUtil";
import { DisplayTotalRow } from "./ProgressGraphTotalRow";
import { trim } from "lodash";

export const DisplayPerformanceGraphs = ({
  progressData,
  stateData,
  setStateData,
  standard,
  schoolData,
  sectionData,
  teacherData,
  isGlobal,
  standardDescription,
  rowsSelectedCount,
}: {
  progressData: ProgressData;
  stateData: ProgressLocalStorageInterface;
  setStateData: React.Dispatch<
    React.SetStateAction<ProgressLocalStorageInterface | null>
  >;
  schoolData: SchoolData[];
  sectionData: SectionData[];
  teacherData: TeacherData[];
  standard?: string;
  FormattedSelection: React.ElementType;
  isGlobal: boolean;
  standardDescription?: string;
  rowsSelectedCount: number;
}) => {
  const [cardData, setCardData] = useState<RowDataInterface[]>();
  const [graphData, setGraphData] = useState<ProgressGraphDataInterface[]>();
  const [showGrid, setShowGrid] = useState(true);
  const [incompleteData, setIncompleteData] = useState<any[]>();
  const [sectionsBySchool, setSectionsBySchool] = useState<any[]>();
  const [totalData, setTotalData] = useState<RowDataInterface>();
  const [displayMonths, setDisplayMonths] = useState<Date[]>([]);

  // Initialize display months based on the start and end dates from stateData
  useEffect(() => {
    if (stateData.startDate && stateData.endDate) {
      setDisplayMonths(
        initializeDisplayMonths(stateData.startDate, stateData.endDate)
      );
    }
  }, [stateData.startDate, stateData.endDate]);

  useEffect(() => {
    // API call to /progress was successful
    if (progressData) {
      const displayData: RowDataInterface[] = [];
      const newGraphData: any = [];
      const newIncompleteData: any = [];
      const schoolsArray: Array<SchoolData & { sections?: SectionData[] }> = [];

      if (Object.keys(progressData).length) {
        Object.keys(progressData).forEach((key) => {
          const typedKey = key as keyof ProgressData;
          const newData = initializeCardData(
            displayMonths,
            progressData[typedKey],
            DAY
          );
          // set "Overall" data as start of array
          newData.unshift({
            month: "Overall",
            data: {
              numStudentAssignments:
                progressData[typedKey].total.numStudentAssignments,
              numStudents: progressData[typedKey].total.numStudents,
              flu:
                progressData[typedKey].total.flu ||
                (progressData[typedKey].total.numStudentAssignments ? 0 : "--"),
              perc:
                progressData[typedKey].total.perc ||
                (progressData[typedKey].total.numStudentAssignments ? 0 : "--"),
            },
          });

          const section = sectionData.find(
            (sec) => sec.sectionId === parseInt(key)
          );
          if (section) {
            const getSchoolBySectionId = (secId: any) => {
              const section = sectionData.find(
                (sec: any) => sec.sectionId === parseInt(secId)
              );
              if (section && Array.isArray(section.schools)) {
                const school = schoolData.find(
                  (sch: any) => section.schools.indexOf(sch.schoolid) >= 0
                );
                return school;
              }
            };
            const school = getSchoolBySectionId(key);
            if (stateData.scope === "Sections") {
              key !== "data" &&
                !objectInArray(school, schoolsArray, "_id") &&
                school &&
                schoolsArray.push(school);
            }

            schoolsArray.forEach((school) => {
              if (section.schools.includes(school.schoolid)) {
                Array.isArray(school.sections) &&
                !objectInArray(section, school.sections, "sectionId")
                  ? school["sections"].push(section)
                  : (school["sections"] = [section]);
              }
            });
          }

          const dataLabel = getDataLabel(
            key,
            stateData,
            schoolData,
            teacherData,
            sectionData
          );

          // only add to chart for individual selections (School, Teacher, Section) that have Student Count data
          if (dataLabel !== "Total") {
            if (progressData[typedKey].total.numStudentAssignments) {
              newGraphData.push({
                label: parseDemoProps({
                  label: dataLabel,
                  dataScope: stateData.scope,
                }),
                data: {
                  flu: progressData[typedKey].total.flu || 0,
                  perc: progressData[typedKey].total.perc || 0,
                  problemsPerStudent:
                    progressData[typedKey].total.numStudentAssignments /
                    progressData[typedKey].total.numStudents,
                },
              });
            } else
              newIncompleteData.push({
                label: parseDemoProps({
                  label: dataLabel,
                  dataScope: stateData.scope,
                }),

                data: {
                  flu: progressData[typedKey].total.flu || 0,
                  perc: progressData[typedKey].total.perc || 0,
                  problemsPerStudent: Math.floor(
                    progressData[typedKey].total.numStudentAssignments /
                      progressData[typedKey].total.numStudents
                  ),
                },
              });
          }

          /**
           *
           * @param teachId the teacher id whose school you would like to find
           * @return first School found that matches an entry on the teachers SchoolPlus array
           */
          const getTeacherSchool: any = (teachId: string) => {
            const teacherInfo = teacherData.find(
              (teacher) => teacher._id === parseInt(teachId)
            );
            const teacherSchoolId = teacherInfo?.schoolPlus.find((id) =>
              schoolData.find((school) => id === school.schoolid)
            );
            const schoolInfo = schoolData.find(
              (school) => teacherSchoolId === school.schoolid
            );
            return schoolInfo;
          };

          /**
           *
           * @param secId section ID
           * @returns array of teacher names for the requested section
           */
          const getSectionTeachers: any = (secId: string) => {
            const sectionInfo = sectionData.find(
              (section) => section.sectionId === parseInt(secId)
            );
            return sectionInfo?.teachers.map((teacher) => teacher.name);
          };

          const metaLabel =
            key === "data"
              ? null
              : stateData.scope === "Schools"
              ? null
              : stateData.scope === "Teachers"
              ? {
                  schools: getTeacherSchool(key)?.schoolName || null,
                }
              : stateData.scope === "Sections"
              ? {
                  teachers: getSectionTeachers(key) || [],
                  schools: sectionData
                    .find((section) => section.sectionId === parseInt(key))
                    ?.schools.map(
                      (schoolID) =>
                        schoolData.find(
                          (school) => school.schoolid === schoolID
                        )?.schoolName
                    ),
                }
              : null;

          displayData.push({
            key: key,
            label: dataLabel,
            cardData: newData,
            meta: metaLabel,
          });
        });
      }

      const sortedData: RowDataInterface[] = displayData.sort(sortByFluency);

      if (schoolsArray.length > 1) {
        /**
         * sort schools array by the highest fluency value of their associated sections
         * @param a school to compare
         * @param b school to compare
         */
        const sortFn = (a: SchoolData, b: SchoolData) => {
          const aIndex = sortedData.findIndex((section: RowDataInterface) =>
            section.meta?.schools?.includes(a.schoolName)
          );
          const bIndex = sortedData.findIndex((section: RowDataInterface) =>
            section.meta?.schools?.includes(b.schoolName)
          );
          return aIndex < bIndex ? -1 : aIndex > bIndex ? 1 : 0;
        };
        schoolsArray.sort(sortFn);
        setSectionsBySchool(schoolsArray);
      }

      setCardData(sortedData);
      setGraphData(newGraphData);
      setIncompleteData(newIncompleteData);
    }
  }, [progressData, displayMonths]);

  useEffect(() => {
    if (cardData) {
      setTotalData(
        cardData.find((row: RowDataInterface) => row.label === "Total")
      );
    }
  }, [cardData]);

  return (
    <>
      {/* Register the controls */}
      {cardData && Array.isArray(cardData) && (
        <div>
          <div className="flex w-full justify-between">
            <div className="inline-flex flex-col items-start justify-start gap-2">
              <div className="font-['Lora'] text-lg font-bold leading-relaxed text-neutral-900">
                {standard}
              </div>
              <div className="mr-4 max-w-lg text-justify font-['Lato'] text-sm font-normal leading-relaxed text-neutral-900">
                {standardDescription}
              </div>
            </div>
            <div className="space-x-14 sm:flex xl:justify-between">
              <>
                {!isGlobal && (
                  <DeltaMathToggle
                    optionA="Fluency"
                    optionB="Completion"
                    aSelected={stateData.axisOption === "Fluency"}
                    onChangeFn={() => {
                      setStateData({
                        ...stateData,
                        axisOption:
                          stateData.axisOption === "Fluency"
                            ? "Completion"
                            : "Fluency",
                      });
                    }}
                  />
                )}
                <div className="py-2">
                  <Tooltip message="Grid">
                    <button onClick={() => setShowGrid(!showGrid)}>
                      <GridIcon
                        classes={
                          showGrid ? "stroke-dm-gray-600" : "stroke-dm-gray-200"
                        }
                      />
                    </button>
                  </Tooltip>

                  <Tooltip message={"Graph"}>
                    <button
                      disabled={
                        Array.isArray(graphData) && graphData.length < 2
                      }
                      onClick={() => setShowGrid(!showGrid)}
                    >
                      <GraphIcon
                        classes={
                          showGrid ? "fill-dm-gray-200" : "fill-dm-gray-600"
                        }
                      />
                    </button>
                  </Tooltip>
                </div>
              </>
            </div>
          </div>
        </div>
      )}

      {/* Display the Graphs */}
      {graphData && Array.isArray(graphData) && !showGrid && (
        <Transition
          as="div"
          show={true}
          appear={true}
          enter="transition-opacity duration-300"
          enterFrom="transform opacity-0"
          enterTo="transform opacity-100"
          leave="transition-opacity duration-300"
          leaveFrom="transform opacity-100"
          leaveTo="transform opacity-0"
        >
          <div className="grid grid-flow-row">
            <ProgressScatterPlot
              title={`${stateData.axisOption} by ${stateData.scope.slice(
                0,
                stateData.scope.length - 1
              )} for Selected Standard`}
              data={graphData}
              yAxisVariable={
                stateData.axisOption === "Fluency" ? "flu" : "perc"
              }
            />
            {Array.isArray(incompleteData) && incompleteData.length > 0 && (
              <div className="mx-8 text-gray-500">
                <p className="text-md font-semibold">{`${stateData.scope} Selected with No Data`}</p>
                <ul>
                  {incompleteData
                    .map(
                      (dataPoint: ProgressGraphDataInterface) => dataPoint.label
                    )
                    .join(", ")}
                </ul>
              </div>
            )}
          </div>
        </Transition>
      )}

      {/* The section by school display */}
      {cardData &&
        Array.isArray(cardData) &&
        showGrid &&
        Array.isArray(sectionsBySchool) &&
        sectionsBySchool.length > 1 && (
          // display school accordions if many sections were selected
          <>
            <Transition
              as="div"
              show={true}
              appear={true}
              enter="transition-opacity duration-300"
              enterFrom="transform opacity-0"
              enterTo="transform opacity-100"
              leave="transition-opacity duration-300"
              leaveFrom="transform opacity-100"
              leaveTo="transform opacity-0"
            >
              <div className="overflow-x-auto">
                {totalData && (
                  <DisplayTotalRow
                    rowData={totalData}
                    showFluency={stateData.axisOption === "Fluency"}
                    stateData={stateData}
                    cardData={cardData[0].cardData}
                  />
                )}
              </div>
              {cardData.length !== 0 &&
                sectionsBySchool.map(
                  (school: SchoolData & { sections?: SectionData[] }) => {
                    return (
                      <DeltaMathAccordion
                        key={school.schoolName}
                        title={school.schoolName}
                        renderExpanded={true}
                        content={
                          <div>
                            {cardData.map((row: RowDataInterface) => (
                              <div key={row.key}>
                                {school.sections?.map((section) => {
                                  if (parseInt(row.key) === section.sectionId) {
                                    const acc: number[] = [];
                                    row.cardData.map((item: any) => {
                                      if (item.month === "Overall") acc.push(0);
                                      else if (item.data.studentCount === "--")
                                        acc.push(0);
                                      else acc.push(item.data.studentCount);
                                    });
                                    return (
                                      <ul
                                        className=""
                                        key={row.key + section.sectionId}
                                      >
                                        <div className="grid grid-cols-1 sm:grid-cols-8">
                                          <ProgressHeaderCard
                                            dataScope={stateData.scope}
                                            title={row.label}
                                            meta={row.meta}
                                            sameSchool={false}
                                          />
                                          <li
                                            key={row.key}
                                            className={`grid-cols-${cardData.length} ml-2 border-b-2 sm:col-span-7`}
                                          >
                                            <div className="mt-2">
                                              <div className="mt-2 grid grid-flow-col">
                                                {row.cardData.map((card) => (
                                                  <ProgressDataCard
                                                    key={
                                                      row.key +
                                                      card.month +
                                                      card.data.flu.toString() +
                                                      card.data.perc.toString()
                                                    }
                                                    title={card.month}
                                                    stats={card?.data}
                                                    showFluency={
                                                      stateData.axisOption ===
                                                      "Fluency"
                                                    }
                                                    isTotal={
                                                      row.label === "Total"
                                                        ? true
                                                        : false
                                                    }
                                                    isOverall={
                                                      card.month === "Overall"
                                                    }
                                                    allValuesInRow={acc}
                                                  />
                                                ))}
                                              </div>
                                            </div>
                                          </li>
                                        </div>
                                      </ul>
                                    );
                                  }
                                })}
                              </div>
                            ))}
                          </div>
                        }
                        padding={16}
                        showExpand={true}
                      />
                    );
                  }
                )}
            </Transition>
          </>
        )}

      {/* The standard display */}
      {cardData && Array.isArray(cardData) && !sectionsBySchool && showGrid && (
        <Transition
          as="div"
          show={true}
          appear={true}
          enter="transition-opacity duration-300"
          enterFrom="transform opacity-0"
          enterTo="transform opacity-100"
          leave="transition-opacity duration-300"
          leaveFrom="transform opacity-100"
          leaveTo="transform opacity-0"
        >
          <div className="mt-4 overflow-x-auto">
            {cardData.length > 0 &&
              cardData.map((row: RowDataInterface) => {
                const acc: number[] = [];
                row.cardData.map((item: any) => {
                  if (item.month === "Overall") acc.push(0);
                  else if (item.data.numStudentAssignments === "--")
                    acc.push(0);
                  else acc.push(item.data.numStudentAssignments);
                });
                return (
                  <ul key={row.key}>
                    <div className="grid grid-cols-12 sm:w-[100%]">
                      <div className="sticky left-0 col-span-1 my-auto h-full bg-white shadow-xl">
                        <ProgressHeaderCard
                          dataScope={stateData.scope}
                          title={row.label}
                          meta={row.meta}
                          sameSchool={true}
                        />
                      </div>
                      <li
                        key={row.key}
                        className={`grid-cols-${cardData.length} col-span-11 border-b-2`}
                      >
                        <div className="mt-2">
                          <div className="flex flex-row gap-0">
                            {row.cardData.map((card) => (
                              <ProgressDataCard
                                key={
                                  row.key +
                                  card.month +
                                  card.data.flu.toString() +
                                  card.data.perc.toString()
                                }
                                title={card.month}
                                stats={card?.data}
                                showFluency={stateData.axisOption === "Fluency"}
                                isTotal={row.label === "Total" ? true : false}
                                isOverall={card.month === "Overall"}
                                allValuesInRow={acc}
                              />
                            ))}
                          </div>
                        </div>
                      </li>
                    </div>
                  </ul>
                );
              })}
          </div>
          {rowsSelectedCount > ProgressSeriesLimit && (
            <div>
              <p className="mt-2">{`Compare up to ${ProgressSeriesLimit} ${stateData.scope} at a time.`}</p>
            </div>
          )}
        </Transition>
      )}
    </>
  );
};

const parseDemoProps = ({
  label,
  dataScope,
}: {
  label: string;
  dataScope: string;
}) => {
  let result;
  switch (dataScope) {
    case "Schools":
      result = demoString({ value: label, type: "school_name" });
      break;
    case "Teachers":
      {
        const fullName = label.split(" ");
        const first = demoString({ value: fullName[0], type: "person_first" });
        const last = demoString({ value: fullName[1], type: "person_last" });
        result = first + " " + last;
      }
      break;
    case "Sections":
      result = demoString({ value: label, type: "section_name" });
      break;
    default:
      result = "";
      break;
  }
  return result;
};

/**
 * check if an object with a given key is in an array of objects
 * @param obj an object
 * @param list an array (of objects)
 * @param key key value of the object to search for
 * @returns boolean true if an object with the specified key value is found, otherwise false
 */
const objectInArray = (obj: any, list: any[], key: string) => {
  let i;
  for (i = 0; i < list.length; i++) {
    if (list[i][key] === obj[key]) {
      return true;
    }
  }
  return false;
};

/**
 * sort function -- Array.sort(sortByFluency)
 * @param a an array of CardData representing a "row."  The first element should be the "Overall" (average) data of the remaining elements
 * @param b an array of CardData representing a "row."  The first element should be the "Overall" (average) data of the remaining elements
 * @returns an array of CardDataInterface[] sorted by the fluency value of each array element's first index.
 */
const sortByFluency = (
  a: { cardData: ProgressMonthData[]; label: string },
  b: { cardData: ProgressMonthData[]; label: string }
) => {
  // Always keep 'Total' at the top
  if (a.label === "Total") return -1;
  if (b.label === "Total") return 1;

  const fluA = a.cardData[0].data.flu === "--" ? 0 : a.cardData[0].data.flu;
  const fluB = b.cardData[0].data.flu === "--" ? 0 : b.cardData[0].data.flu;

  return fluA > fluB ? -1 : fluA < fluB ? 1 : 0;
};

/**
 * Helper function to initialize the display months based on the start and end dates.
 * @param startDate The start date of the interval to display months for.
 * @param endDate The end date of the interval to display months for.
 * @returns
 */
function initializeDisplayMonths(startDate: Date, endDate: Date): Date[] {
  const months = eachMonthOfInterval({
    start: startDate,
    end: endDate,
  });
  return months.map(
    (month: Date) =>
      new Date(
        formatInTimeZone(month, "America/New_York", "yyyy-MM-dd HH:mm:ss")
      )
  );
}

/**
 * Maps the data received over the /progress endpoint to an array of ProgressMonthData objects
 * used in rendering the progress data cards.
 * @param displayMonths The array of months to display in the progress data cards.
 * @param progressDataEntry The progress data object for a given key (e.g., section, teacher, etc).
 * @param DAY The time window (in seconds) to match a progress entry to a month.
 * @returns Array fo MonthData objects for each display month.
 */
function initializeCardData(
  displayMonths: Date[],
  progressDataEntry: { [timestamp: string]: ProgressMonthDataEntry },
  DAY: number
): ProgressMonthData[] {
  return displayMonths.map((date) => {
    let thisEntry: Record<
      keyof Omit<ProgressDataEntry, "sections">,
      string | number
    > = {
      flu: "--",
      perc: "--",
      numStudentAssignments: "--",
      numStudents: "--",
    };
    const displayMonthEpochTime = date.getTime() / 1000;
    const progressDataKeys = Object.keys(progressDataEntry).filter(
      (e) => e !== "total"
    );
    for (const progressTimestampString of progressDataKeys) {
      const parsedProgressTimestamp = parseInt(progressTimestampString);
      if (Math.abs(displayMonthEpochTime - parsedProgressTimestamp) <= DAY) {
        thisEntry = progressDataEntry[parsedProgressTimestamp];
        break;
      }
    }
    return {
      month: `${date.toLocaleString("en-US", {
        month: "short",
      })} ${date.getFullYear()}`,
      data: thisEntry,
    };
  });
}

/**
 * Helper function to get the label for a data point based on the key, scope, and available data.
 * @param key The key of the data point (e.g., school ID, teacher ID, section ID, etc.)
 * @param stateData The current state data containing the scope of the report (Schools, Teachers, Sections).
 * @param schoolData The array of school data.
 * @param teacherData The array of teacher data.
 * @param sectionData The array of section data.
 * @returns A string representing the label for the data point based on the key and scope.
 */
function getDataLabel(
  key: string,
  stateData: ProgressLocalStorageInterface,
  schoolData: SchoolData[],
  teacherData: TeacherData[],
  sectionData: SectionData[]
): string {
  if (key === "data") return "Total";

  const trimUndefined = (str: string | undefined) => {
    return str ? trim(str) : "";
  };

  switch (stateData.scope) {
    case "Schools": {
      const school = schoolData.find((s) => s.schoolid === key);
      return school?.schoolName || "Total";
    }
    case "Teachers": {
      const teacher = teacherData.find((t) => t._id === parseInt(key));
      if (teacher)
        return `${trimUndefined(teacher.first)} ${trimUndefined(teacher.last)}`;
      return "Total";
    }
    case "Sections": {
      const section = sectionData.find((s) => s.sectionId === parseInt(key));
      return section?.sectionName || "Total";
    }
    default:
      return "Total";
  }
}
